/**
 * 处理 url 参数
 */

import chatStoreManager from './manager';
import localRobot from '@/common/localRobot';
import { makeAutoObservable, runInAction } from 'mobx';
import { request } from '@/common';
import { getAgentInfo, getUnLoginedAgentInfo } from '@/services/chat';
import { createSlsLogger } from '@public/log/sls';
import { EE, HelperCore } from '@/common/dependencies';
import globalStore from '../global';
import Conf from '@/common/conf';
import navbarStore from '../navbar';
import userStore from '../user';
import client from '../client';
import inputTypeStore from '@/components/input-file/input-type';
import { AssistantType } from '@shared/constants/chat';
import apiRobot from '@/common/apiRobot';

const slsLogger = createSlsLogger("urlParams");
const MAX_HISTORY_ROUTER_COUNT = 10;

class Store {
  agentList = {};
  knowledgeChatWindow = null;
  /**
   * 记录最新的 N 个 pathname
   */
  historyRouters = [];
  /**
   * 按左侧导航，记录最后的 pathname
   */
  lastRouterByType = {};

  constructor () {
    makeAutoObservable(this);

    if(!HelperCore.IS_PROD){
      window._urlStore = this;
    }

    this.init();
  }

  /**
   * 智能体会话
   * @param {String|Number} agentId 
   * @param {?String} prompt 
   * @param {?Boolean} supportUnLogined 见文档 https://axihpnrpdh3.feishu.cn/wiki/JlfqwTxewieOm4k6Qq4cJS2tnhf
   */
  newAgentChat (agentId, prompt, supportUnLogined) {
    if(!agentId){
      return;
    }

    (supportUnLogined ? getUnLoginedAgentInfo : getAgentInfo)({ agent_id: agentId, query_type: 'all' })
    .then(res => {
      if(res?.data?.data?.agent_id) {
        slsLogger.log({
          key : "query_q",
          action : "query_agent",
          ext : agentId,
          query : prompt,
        });

        runInAction(() => {
          this.agentList[agentId] = res.data.data;
        });

        chatStoreManager.newAgentChat({agentId : Number(agentId), agentInfo: res.data.data, prompt})
      }
      else {
        slsLogger.log({
          key : "query_q",
          action : "error_agent_detail",
          arrt : "api",
          ext : JSON.stringify(res),
        });
      }
    })
    .catch(res => {
      slsLogger.log({
        key : "query_q",
        action : "error_agent_detail",
        arrt : "catch",
        ext : res.stack || JSON.stringify(res),
      });
    });
  }

  /**
   * 新版智能体会话
   */
  newAiAgentChat (aiAgentId, prompt, sendParams) {
    chatStoreManager.newAiAgentChat({aiAgentId, prompt, sendParams});
  }

  /**
   * 模型会话
   * @param {String} roleId 
   */
  newRobotChat (roleId, prompt) {
    navbarStore.getSpecifyModel(roleId)
    .then(() => {
      if(navbarStore.chatModels[roleId]){
        const store = chatStoreManager.newChatStore({ asstId : roleId });
        globalStore.navigate("/chat");

        slsLogger.log({
          key : "query_q",
          action : "query_robot",
          ext : roleId,
          query : prompt,
        });
    
        if(prompt){
          store.sendMessage({
            input : prompt,
          });
        }
      }
      else {
        slsLogger.log({
          key : "query_q",
          action : "error_robot_detail",
          arrt : "api",
          ext : roleId,
        });
      }
    })
    .catch(res => {
      slsLogger.log({
        key : "query_q",
        action : "error_robot_detail",
        arrt : "catch",
        ext : res.stack || JSON.stringify(res),
      });
    });
  }

  /**
   * 本地模型会话
   */
  newLocalChat (roleId, prompt) {
    localRobot.whenInitReady(() => {
      if(localRobot.localModels.find(item => item.id == roleId)){
        const store = chatStoreManager.newChatStore({ type: AssistantType.LocalModel, asstId : roleId });
        globalStore.navigate("/chat");

        slsLogger.log({
          key : "query_q",
          action : "query_local",
          ext : roleId,
          query : prompt,
        });
    
        if(prompt){
          store.sendMessage({
            input : prompt,
          });
        }
      }
      else {
        slsLogger.log({
          key : "query_q",
          action : "error_local_detail",
          arrt : "api",
          ext : roleId,
        });
      }
    })
  }

  /**
   * 云模型、API 模型、局域网模型、本地模型、智能体
   */
  newChat ({ roleId, agentId }, prompt) {
    if(agentId){
      this.newAgentChat(agentId, prompt);
    }
    else if(localRobot.localModels.find(item => item.id == roleId)){
      this.newLocalChat(roleId, prompt);
    }
    else {
      this.newRobotChat(roleId, prompt);
    }
  }

  init () {
    window.addEventListener('message', this.messageHandler);
    window._wsClientMsg2 = res => {
      if(this.knowledgeChatWindow){
        this.knowledgeChatWindow.postMessage({
          action : "_wsClientMsg",
          data : res,
        }, "*");
      }
      else {
        HelperCore.log(`_wsClientMsg2 knowledgeChatWindow null`, this.knowledgeChatWindow, res);
      }
    }
    window._wsAPIClientMsg2 = res => {
      if(this.knowledgeChatWindow){
        this.knowledgeChatWindow.postMessage({
          action : "_wsAPIClientMsg",
          data : res,
        }, "*");
      }
      else {
        HelperCore.log(`_wsAPIClientMsg2 knowledgeChatWindow null`, this.knowledgeChatWindow, res);
      }
    }

    const pathname = location.pathname;
    if(pathname === "/" || pathname === "/chathome"){
      this.handleParams();
    }
  }

  handleParams = () => {
    globalStore.whenCloudReady(() => {
      const urlParams = HelperCore.getParams();
      const {
        agent_id,
        role_id,
        llm_id,
        q,
      } = urlParams;

      if(agent_id || role_id || llm_id){
        slsLogger.log({
          key : "query_q",
          action : "custom_model",
          query : q || "",
        });
      }

      if(agent_id){
        this.newAgentChat(agent_id, q, true);
        return;
      }
      if(role_id){
        this.newRobotChat(role_id, q);
        return;
      }
      
      if(llm_id){
        const robot = localRobot.getRobotByModelName(llm_id);
        if (robot) {
          this.newLocalChat(robot.id, q);
        }
        return;
      }
      if(q){
        this.newRobotChat(Conf.roleIds.deepseek, q);
        return;
      }
    })
  }

  messageHandler = (event) => {
    const { origin, source, data:eventData } = event;
    const { action, data } = eventData || {};
    switch(action){
      /**
       * 智能体 iframe，打开智能体会话
       */
      case "newAgentChat":{
        this.reportMessage(event);
        this.newAgentChat(data.id, data.prompt);
        break;
      }
      /**
       * 打开工具，等同于点击左侧栏
       * 智能体 iframe，打开知识库
       */
      case "openTools":{
        this.reportMessage(event);
        const { type } = data;
        this.handleTools(type);
        break;
      }
      /**
       * iframe 内登录过期
       * 调起客户端登录
       */
      case "needLogin":{
        // debug
        HelperCore.log(`messageHandler needLogin:`, event);

        // 主页面已登录，子页面登录态异常
        if(userStore.isUser){
          slsLogger.log({
            key : "client",
            action : "login_status_error",
            ext : origin,
          });
        }

        this.reportMessage(event);
        userStore.gotoLogin(true, origin);
        break;
      }
      /**
       * iframe，查询本地模型
       */
      case "getLocalList":{
        this.reportMessage(event);
        source.postMessage({ 
          action: 'sendLocalList', 
          data: JSON.stringify(localRobot.localModels || []),
        }, '*');

        try{
          const url = new URL(origin);
          if(url.host == location.host && source.location.pathname == "/knowledge/chat"){
            this.knowledgeChatWindow = source;
          }
        } catch(e){}
        break;
      }
      /**
       * iframe，查询 API 模型
       */
      case "getAPIModelList":{
        this.reportMessage(event);
        source.postMessage({ 
          action: 'sendAPIModelList', 
          data: JSON.stringify(Object.values(navbarStore.chatModels).filter(item => item.chat_type == AssistantType.APIModel)),
        }, '*');
        break;
      }
      /**
       * iframe页面通知修改标题栏信息：{title:'',icon:''}
       */
      case "changeDocumentInfo":{
        this.reportMessage(event);
        globalStore.setDocumentInfo(data)
        break;
      }
      /**
       * 知识库对话页，获取焦点
       */
      case "chatInputFocus":{
        EE.emit("chat_input_focus");
        break;
      }
      case "createAgent": {
        const {server_id} = data
        client.openPage(`/tools/aiagent/manage?server_id=${server_id}`)
        // globalStore.navigate(`/tools/aiagent/manage?server_id=${server_id}`);
        break;
      }
    }
  }
  reportMessage = (event) => {
    const { origin, data } = event;
    slsLogger.log({
      key : "postMessage",
      ext : JSON.stringify(data),
      ext1 : origin,
    });
  }

  handleTools = (type) => {
    const handleSwitch = (item) => {
      switch(item.open_type){
        // AI tab
        case "1":{
          client.openPage(item.url);
          break;
        } 
        // 普通 tab
        case "2":{
          client.openPage(item.url, false);
          break;
        }
        // iframe
        case "3":{
          inputTypeStore.reset();
          globalStore.navigate(`/tools/${item.type}`);
          break;
        }
        // 页面跳转
        case "4":{
          if(this.lastRouterByType[type]){
            globalStore.navigate(this.lastRouterByType[type]);  
          }
          else {
            globalStore.navigate(item.url);      
          }

          // globalStore.navigate(item.url);  
          break;
        }
      }
    }
    if(type){
      const item = (globalStore.cloudConfig.assistantLeftSideTools || []).find(item => item.type == type);
      if(item){
        if([
          // 部分不需要登录
          "webpage", 
          "assistant", 
          "namiso", 
          // 部分自行处理登录
          "knowledge",
        ].includes(item.type)){
          handleSwitch(item);
        }
        else{
          userStore.doWhenLogin(() => {
            handleSwitch(item);
          })
        }
      }
    }
  }

  /**
   * URL 变化时，重置 knowledgeChatWindow？
   * 不能重置，iframe 隐藏不销毁
   */
  clearKnowledgeChatWindow = () => {
    this.knowledgeChatWindow = null;
  }

  /**
   * 记录历史
   * @param {Location} location 
   */
  handleLocationChange (location) {
    const {
      pathname,
    } = location;

    this.historyRouters.push(pathname);
    this.historyRouters = this.historyRouters.slice(-MAX_HISTORY_ROUTER_COUNT);

    const type = this.getLocationType(pathname);
    if(type){
      this.lastRouterByType = {
        ...this.lastRouterByType,
        ...{
          [type] : pathname,
        },
      }
    }
  }
  getLocationType = (pathname) => {
    if(!pathname){
      return null;
    }
    // 智能体
    if(
      // 首页
      pathname == "/tools/aiagent"
      // 案例/我的
      || /^\/tools\/aiagent\/(case|mine)/.test(pathname)
    ){
      return "aiagent";
    }
    return null;
  }
  getChatPrevRouter (defaultRouter = "/") {
    const historyRouters = [...this.historyRouters];
    historyRouters.reverse();
    let lastRouter = defaultRouter;
    // 360se
    if (client.isSeLLQ) {
      lastRouter = '/tools/aiagent'
    }
    historyRouters.some((pathname, index) => {
      if(
        [
          "/",                // 首页
          "/tools/aiagent",   // 智能体首页
        ].includes(pathname)
        // 找前 n- 1 级就可以了
        && index < 3
      ){
        lastRouter = pathname;
        return true;
      }
    });

    return lastRouter;
  }
}

const urlStore = new Store();
export default urlStore;